import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Truck, Upload, History, ChevronDown, ChevronUp, Search, Download, FileWarning } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const COD_STATUS_STYLES = {
  complete: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  partial: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  pending: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40',
};
const REMIT_STYLES = {
  received: 'text-emerald-400', short: 'text-amber-400',
  approved_short: 'text-blue-400', pending: 'text-wt-text-muted',
};

export default function CourierCodPanel({ formatCurrency }) {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('');
  const [batches, setBatches] = useState(null);
  const [shortfallFor, setShortfallFor] = useState(null);
  const [shortfallReason, setShortfallReason] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      const resp = await axios.get(`${API}/courier-cod/summary`, { ...auth(), params });
      setData(resp.data);
    } catch (e) { /* no permission */ }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const resp = await axios.post(`${API}/courier-cod/upload`, fd, auth());
      setResult(resp.data);
      const b = resp.data.batch;
      toast.success(`Processed: ${b.matched} matched, ${b.unmatched.length} unmatched, ${b.duplicates} duplicate`);
      if (b.orders_paid.length) toast.success(`${b.orders_paid.length} order(s) fully received & marked paid: ${b.orders_paid.join(', ')}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const approveShortfall = async () => {
    try {
      await axios.post(`${API}/courier-cod/shipments/${shortfallFor.id}/approve-shortfall`,
        { reason: shortfallReason }, auth());
      toast.success('Shortfall approved — counted as fully received');
      setShortfallFor(null); setShortfallReason('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadBatches = async () => {
    try {
      const resp = await axios.get(`${API}/courier-cod/batches`, auth());
      setBatches(resp.data.batches || []);
    } catch { toast.error('Failed to load history'); }
  };

  const downloadErrors = async (b) => {
    try {
      const r = await axios.get(`${API}/courier-cod/batches/${b.id}/errors`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `cod-errors-${b.filename || 'batch'}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download errors'); }
  };

  if (!data) return null;
  const { stats } = data;
  const orders = filter ? data.orders.filter((o) => o.cod_status === filter) : data.orders;

  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-6" data-testid="courier-cod-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-orange-500/10"><Truck className="h-6 w-6 text-orange-400" /></div>
          <div>
            <h2 className="text-xl font-semibold text-wt-text-primary">Courier COD Remittance</h2>
            <p className="text-wt-text-muted text-sm">Upload the courier's AWB + amount sheet to reconcile cash payouts</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input type="file" id="cod-file" className="hidden" accept=".csv,.xlsx,.xls,.txt" onChange={onUpload} />
          <Button size="sm" disabled={uploading} onClick={() => document.getElementById('cod-file').click()} data-testid="cod-upload-btn">
            <Upload className="w-4 h-4 mr-1" />{uploading ? 'Uploading…' : 'Upload CSV / Excel'}
          </Button>
          <Button size="sm" variant="outline" className="border-wt-border" onClick={loadBatches} data-testid="cod-history-btn">
            <History className="w-4 h-4 mr-1" />History
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cod-stats">
        <button className="rounded-lg border border-wt-border/50 p-3 text-left" onClick={() => setFilter('')}>
          <p className="text-[11px] text-wt-text-muted">COD not yet received</p>
          <p className="text-lg font-semibold text-orange-400" data-testid="cod-pending-amount">{formatCurrency(stats.pending_cod)}</p>
        </button>
        <button className="rounded-lg border border-wt-border/50 p-3 text-left" onClick={() => setFilter('complete')}>
          <p className="text-[11px] text-wt-text-muted">Received (complete orders)</p>
          <p className="text-lg font-semibold text-emerald-400">{stats.complete_orders}</p>
        </button>
        <button className="rounded-lg border border-wt-border/50 p-3 text-left" onClick={() => setFilter('partial')}>
          <p className="text-[11px] text-wt-text-muted">Partial (split / short)</p>
          <p className="text-lg font-semibold text-amber-400" data-testid="cod-partial-count">{stats.partial_orders}</p>
        </button>
        <button className="rounded-lg border border-wt-border/50 p-3 text-left" onClick={() => setFilter('pending')}>
          <p className="text-[11px] text-wt-text-muted">Pending orders</p>
          <p className="text-lg font-semibold text-wt-text-primary">{stats.pending_orders}</p>
        </button>
      </div>
      {filter && <p className="text-xs text-wt-text-muted mb-2">Filtered: {filter} — <button className="text-wt-gold" onClick={() => setFilter('')}>clear</button></p>}

      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-wt-text-muted" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9"
          placeholder="Search by order number or courier AWB no…" data-testid="cod-search-input" />
      </div>

      <div className="space-y-2 max-h-[480px] overflow-auto" data-testid="cod-orders-list">
        {orders.map((o) => {
          const key = o.order_id || o.order_number;
          const open = !!expanded[key];
          return (
            <div key={key} className="rounded-lg border border-wt-border/50" data-testid={`cod-order-${o.order_number}`}>
              <button type="button" className="w-full flex items-center justify-between p-3 text-left"
                onClick={() => setExpanded({ ...expanded, [key]: !open })}>
                <span className="text-sm text-wt-text-primary font-medium">
                  {o.order_number}
                  <span className="text-wt-text-muted font-normal"> · {o.customer_name || '—'} · {o.shipments.length} shipment{o.shipments.length === 1 ? '' : 's'}</span>
                </span>
                <span className="flex items-center gap-2 text-xs">
                  <span className="text-wt-text-muted">{formatCurrency(o.received)} / {formatCurrency(o.expected)}</span>
                  <Badge className={`text-[10px] ${COD_STATUS_STYLES[o.cod_status]}`}>{o.cod_status}</Badge>
                  {o.payment_status === 'paid' && <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">order paid</Badge>}
                  {open ? <ChevronUp className="w-3.5 h-3.5 text-wt-text-muted" /> : <ChevronDown className="w-3.5 h-3.5 text-wt-text-muted" />}
                </span>
              </button>
              {open && (
                <div className="px-3 pb-3 space-y-1">
                  {o.shipments.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-xs bg-wt-bg/40 rounded px-2 py-1.5" data-testid={`cod-shipment-${s.awb_number}`}>
                      <span className="text-wt-text-primary">
                        AWB {s.awb_number}<span className="text-wt-text-muted"> · {s.courier_type} · {s.status}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={REMIT_STYLES[s.remit_status]}>
                          {formatCurrency(s.remitted_amount)} / {formatCurrency(s.cod_amount)} · {s.remit_status.replace('_', ' ')}
                        </span>
                        {s.remit_status === 'short' && (
                          <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] border-amber-500/40 text-amber-400"
                            onClick={() => setShortfallFor(s)} data-testid={`approve-shortfall-${s.awb_number}`}>
                            Approve shortfall
                          </Button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-sm text-wt-text-muted text-center py-6">No courier COD shipments{filter ? ` with status ${filter}` : ' yet'}</p>}
      </div>

      {/* Upload result dialog */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Upload Result — {result?.batch?.filename}</DialogTitle></DialogHeader>
          {result && (
            <div className="text-sm space-y-1 text-wt-text-primary" data-testid="cod-upload-result">
              <div className="flex justify-between"><span className="text-wt-text-muted">Rows processed</span><span>{result.batch.rows_total}</span></div>
              <div className="flex justify-between"><span className="text-wt-text-muted">Matched (recorded)</span><span className="text-emerald-400">{result.batch.matched} · {formatCurrency(result.batch.total_received)}</span></div>
              <div className="flex justify-between"><span className="text-wt-text-muted">Duplicates skipped</span><span>{result.batch.duplicates}</span></div>
              <div className="flex justify-between"><span className="text-wt-text-muted">Invalid rows</span><span>{result.batch.bad_rows}</span></div>
              {result.batch.unmatched.length > 0 && (
                <div><span className="text-red-400">Unmatched AWBs ({result.batch.unmatched.length}):</span>
                  <p className="text-xs text-wt-text-muted break-all">{result.batch.unmatched.slice(0, 30).join(', ')}{result.batch.unmatched.length > 30 ? '…' : ''}</p>
                </div>
              )}
              {result.batch.orders_paid.length > 0 && (
                <p className="text-emerald-400 text-xs">Marked paid (full COD received): {result.batch.orders_paid.join(', ')}</p>
              )}
              {result.batch.orders_partial.length > 0 && (
                <p className="text-amber-400 text-xs">Partial (awaiting more AWBs / approval): {result.batch.orders_partial.join(', ')}</p>
              )}
              {(result.order_results || []).filter((r) => r.shopify_sync).map((r) => (
                <p key={r.order_id} className="text-xs">
                  Shopify: {r.order_number} — <span className={r.shopify_sync.status === 'synced' ? 'text-emerald-400' : 'text-red-400'}>{r.shopify_sync.status}</span>
                  {r.shopify_sync.error && <span className="text-wt-text-muted"> · {r.shopify_sync.error.slice(0, 80)}</span>}
                </p>
              ))}
              {(result.batch.unmatched.length > 0 || result.batch.bad_rows > 0) && (
                <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 mt-2 h-7 text-xs"
                  onClick={() => downloadErrors(result.batch)} data-testid="cod-result-errors-download">
                  <FileWarning className="w-3.5 h-3.5 mr-1" />Download error rows (CSV)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve shortfall dialog */}
      <Dialog open={!!shortfallFor} onOpenChange={(o) => !o && setShortfallFor(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Approve Shortfall — AWB {shortfallFor?.awb_number}</DialogTitle></DialogHeader>
          <p className="text-xs text-wt-text-muted">
            Received {shortfallFor && formatCurrency(shortfallFor.remitted_amount)} of {shortfallFor && formatCurrency(shortfallFor.cod_amount)}.
            Approving counts this shipment as fully received (e.g., courier fees deducted) and may mark the order paid.
          </p>
          <Textarea value={shortfallReason} rows={2} placeholder="Reason (e.g., courier COD fee) *"
            onChange={(e) => setShortfallReason(e.target.value)} data-testid="shortfall-reason-input" />
          <DialogFooter>
            <Button onClick={approveShortfall} disabled={!shortfallReason.trim()} data-testid="shortfall-confirm-btn">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batches history dialog */}
      <Dialog open={batches !== null} onOpenChange={(o) => !o && setBatches(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Upload History</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-auto space-y-2">
            {(batches || []).map((b) => (
              <div key={b.id} className="rounded border border-wt-border/50 p-2 text-xs text-wt-text-primary">
                <div className="flex justify-between">
                  <span className="font-medium">{b.filename}</span>
                  <span className="text-wt-text-muted">{(b.at || '').slice(0, 16).replace('T', ' ')} · {b.uploaded_by_name}</span>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-wt-text-muted">
                    {b.matched} matched · {formatCurrency(b.total_received)} received · {b.duplicates} dup · {(b.unmatched || []).length} unmatched · {b.bad_rows || 0} invalid
                    {b.orders_paid?.length > 0 && <span className="text-emerald-400"> · paid: {b.orders_paid.join(', ')}</span>}
                  </span>
                  <span className="flex gap-1">
                    {b.file_url && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-wt-text-muted"
                        onClick={() => window.open(BACKEND + b.file_url, '_blank')} data-testid={`cod-batch-file-${b.id}`}>
                        <Download className="w-3 h-3 mr-1" />File
                      </Button>
                    )}
                    {((b.unmatched || []).length > 0 || (b.bad_rows || 0) > 0) && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-amber-400"
                        onClick={() => downloadErrors(b)} data-testid={`cod-batch-errors-${b.id}`}>
                        <FileWarning className="w-3 h-3 mr-1" />Errors CSV
                      </Button>
                    )}
                  </span>
                </div>
              </div>
            ))}
            {(batches || []).length === 0 && <p className="text-sm text-wt-text-muted text-center py-4">No uploads yet</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
